import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { SharedService } from '../services/shared.service';
import { ToastrService } from 'ngx-toastr';

// ── Integration types ─────────────────────────────────────────────────────────

export type ConnectionStatus = 'connected' | 'disconnected' | 'error' | 'checking';

export interface IntegrationState {
    status: ConnectionStatus;
    lastChecked: Date | null;
    connection_checked_at: Date | null;
    error: string | null;
}

// ── Cron types ────────────────────────────────────────────────────────────────

export type CronStatus = 'success' | 'failed' | 'running' | 'unknown';

export interface CronResult {
    /** "delta" | "full_recon" */
    mode?: string | null;
    /** Delta only — start of LW change-detection window */
    window_from?: string | null;
    /** Delta only — end of LW change-detection window */
    window_to?: string | null;
    /** Recon only — number of SE warehouses synced */
    se_warehouses?: number | null;
    pushed?: number | null;
    skipped?: number | null;
    failed?: number | null;
    /** Set when job exits early because no active location mappings exist */
    action_required?: string | null;
}

export interface CronRun {
    id: number | string;
    /** 'inventory_delta_sync' | 'inventory_full_recon' */
    job_name: string;
    status: CronStatus;
    started_at: string | Date;
    finished_at?: string | Date | null;
    duration_seconds?: number | null;
    result?: CronResult | null;
    error_message?: string | null;
    celery_task_id?: string | null;
}

// ── Aggregate stats type ──────────────────────────────────────────────────────

export interface CronAggStats {
    job_name: string;
    total_runs: number;
    successful: number;
    failed_runs: number;
    still_running: number;
    action_required_runs: number;
    avg_duration_sec: number | null;
    max_duration_sec: number | null;
    total_pushed: number;
    total_skipped: number;
    total_sku_failures: number;
}

// ── Sync cursor type ──────────────────────────────────────────────────────────

export interface SyncCursor {
    name: string;
    last_success_at: string | Date;
    minutes_ago: number;
}

// ── Order sync types ──────────────────────────────────────────────────────────

export interface OrderCronResult {
    mode?: string | null;
    window_from?: string | null;
    window_to?: string | null;
    /** Orders created in LW */
    created?: number | null;
    /** Orders updated/reconciled in LW */
    updated?: number | null;
    skipped?: number | null;
    /** Orders quarantined due to missing SKUs */
    quarantined?: number | null;
    failed?: number | null;
    /** Total orders polled from SE */
    polled?: number | null;
}

export interface OrderCronRun {
    id: number | string;
    /** 'order_sync_se_to_lw' | 'order_quarantine_retry' */
    job_name: string;
    status: CronStatus;
    started_at: string | Date;
    finished_at?: string | Date | null;
    duration_seconds?: number | null;
    result?: OrderCronResult | null;
    error_message?: string | null;
    celery_task_id?: string | null;
}

export interface OrderCronAggStats {
    job_name: string;
    total_runs: number | null;
    successful: number | null;
    failed_runs: number | null;
    still_running: number | null;
    avg_duration_sec: number | null;
    max_duration_sec: number | null;
    /** SUM(records_pushed) — created + updated */
    total_pushed: number | null;
    /** SUM(records_skipped) */
    total_skipped: number | null;
    /** SUM(records_failed) — includes quarantined */
    total_sku_failures: number | null;
}

export interface OrderQuarantineSummary {
    total_quarantined: number;
    unresolved: number;
    resolved: number;
}

// ── Location mapping alert ────────────────────────────────────────────────────

export interface LocationMappingAlert {
    id: number;
    /** 'lw' | 'se' */
    side: string;
    lw_location_id?: string | null;
    lw_location_name?: string | null;
    se_location_id?: string | null;
    se_location_name?: string | null;
    is_resolved: boolean;
    created_at?: string | Date | null;
}

@Component({
    selector: 'app-dashboard',
    standalone: true,
    imports: [CommonModule, RouterLink, MatCardModule, MatButtonModule],
    templateUrl: './dashboard.component.html',
    styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    // ── Integration ────────────────────────────────────────────────────────────
    checking = false;
    shipearly: IntegrationState = { status: 'checking', lastChecked: null, connection_checked_at: null, error: null };
    linnworks: IntegrationState = { status: 'checking', lastChecked: null, connection_checked_at: null, error: null };

    // ── Last-run stats ─────────────────────────────────────────────────────────
    loadingStats = false;
    deltaStat: CronRun | null = null;
    reconStat:  CronRun | null = null;

    // ── 7-day aggregates ───────────────────────────────────────────────────────
    loadingAgg = false;
    deltaAgg: CronAggStats | null = null;
    reconAgg:  CronAggStats | null = null;
    helpBoxOpen = false;

    // ── Sync cursors ───────────────────────────────────────────────────────────
    cursorDelta: SyncCursor | null = null;
    cursorRecon:  SyncCursor | null = null;

    // ── Location mapping alerts ────────────────────────────────────────────────
    // ── Order sync — last run ──────────────────────────────────────────────────────────────
    loadingOrderStats = false;
    orderSyncStat: OrderCronRun | null = null;
    orderQuarantineRetryStat: OrderCronRun | null = null;

    // ── Order sync — 7-day aggregates ──────────────────────────────────────────────────
    loadingOrderAgg = false;
    orderSyncAgg: OrderCronAggStats | null = null;
    orderQuarantineRetryAgg: OrderCronAggStats | null = null;

    // ── Order sync — cursor & quarantine ──────────────────────────────────────────────
    cursorOrderSync: SyncCursor | null = null;
    orderQuarantineSummary: OrderQuarantineSummary | null = null;

    // ── Location mapping alerts ────────────────────────────────────────────────────────────────────────────
    locationAlerts: LocationMappingAlert[] = [];

    // Live clock — always a real Date object, formatted via Europe/London
    bstNow: string = '';
    private clockInterval: ReturnType<typeof setInterval> | null = null;

    constructor(
        private sharedService: SharedService,
        private toastr: ToastrService,
    ) {}

    ngOnInit(): void {
        this.checkConnections();
        this.loadCronStats();
        this.loadAggStats();
        this.loadOrderCronStats();
        this.loadOrderAggStats();
        this.bstNow = this.formatDateTime(new Date());
        this.clockInterval = setInterval(() => { this.bstNow = this.formatDateTime(new Date()); }, 1000);
        // this.loadLocationAlerts();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.clockInterval) { clearInterval(this.clockInterval); }
    }

    // ── Location alerts ────────────────────────────────────────────────────────

    loadLocationAlerts(): void {
        this.sharedService.getRequest('/location_mappings/alerts?is_resolved=0')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    this.locationAlerts = res?.items ?? res?.data ?? res ?? [];
                },
                error: () => { /* non-critical, silent */ },
            });
    }

    alertSides(): string[] {
        return [...new Set(this.locationAlerts.map(a => a.side))];
    }

    dismissAlerts(): void {
        this.locationAlerts = [];
    }

    // ── Integration ────────────────────────────────────────────────────────────

    checkConnections(): void {
        this.checking = true;
        this.shipearly = { status: 'checking', lastChecked: null, connection_checked_at: null, error: null };
        this.linnworks = { status: 'checking', lastChecked: null, connection_checked_at: null, error: null };
        const now = new Date();

        forkJoin({
            shipearly: this.sharedService.getRequest('/api_settings/shipearly/status'),
            linnworks: this.sharedService.getRequest('/api_settings/linnworks/status'),
        })
        .pipe(takeUntil(this.destroy$), finalize(() => { this.checking = false; }))
        .subscribe({
            next: (results: any) => {
                this.shipearly = { status: results.shipearly?.connected ? 'connected' : 'disconnected', connection_checked_at: results.shipearly?.connection_checked_at, lastChecked: now, error: null };
                this.linnworks = { status: results.linnworks?.connected ? 'connected' : 'disconnected', connection_checked_at: results.linnworks?.connection_checked_at, lastChecked: now, error: null };
            },
            error: () => this.checkIndividually(now),
        });
    }

    private checkIndividually(now: Date): void {
        this.sharedService.getRequest('/api_settings/shipearly/status')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => { this.shipearly = { status: res?.connected ? 'connected' : 'disconnected', connection_checked_at: res?.connection_checked_at, lastChecked: now, error: null }; },
                error: (err: any) => { this.shipearly = { status: 'error', lastChecked: now, connection_checked_at: null, error: err?.error?.message ?? 'Unable to reach ShipEarly' }; },
            });
        this.sharedService.getRequest('/api_settings/linnworks/status')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => { this.linnworks = { status: res?.connected ? 'connected' : 'disconnected', connection_checked_at: res?.connection_checked_at, lastChecked: now, error: null }; },
                error: (err: any) => { this.linnworks = { status: 'error', lastChecked: now, connection_checked_at: null, error: err?.error?.message ?? 'Unable to reach Linnworks' }; },
            });
    }

    // ── Cron last-run stats ────────────────────────────────────────────────────

    loadCronStats(): void {
        this.loadingStats = true;
        forkJoin({
            delta: this.sharedService.getRequest('/sync_inventory/inventory_delta_sync/last'),
            recon: this.sharedService.getRequest('/sync_inventory/inventory_full_recon/last'),
        })
        .pipe(takeUntil(this.destroy$), finalize(() => { this.loadingStats = false; }))
        .subscribe({
            next: (res: any) => {
                this.deltaStat = this.mapRun(res.delta);
                this.reconStat  = this.mapRun(res.recon);
            },
            error: () => { this.toastr.error('Could not load cron stats.'); },
        });
    }

    /**
     * Normalises a raw API response into a CronRun.
     * Handles field name differences:
     *   completed_at   → finished_at
     *   result_summary → result  (may arrive as a JSON string — parsed here)
     */
    private mapRun(raw: any): CronRun | null {
        if (!raw) return null;

        // result_summary can be a JSON string e.g. '{"pushed":0,"skipped":1497}'
        // Parse it into an object so template bindings like run.result?.pushed work.
        let result = raw.result ?? null;
        if (!result && raw.result_summary) {
            if (typeof raw.result_summary === 'string') {
                try { result = JSON.parse(raw.result_summary); }
                catch { result = null; }
            } else {
                result = raw.result_summary;
            }
        }

        return {
            ...raw,
            finished_at: raw.finished_at ?? raw.completed_at ?? null,
            result,
        };
    }

    // ── Order sync — last run ─────────────────────────────────────────────────────────────────────────

    loadOrderCronStats(): void {
        this.loadingOrderStats = true;
        forkJoin({
            orderSync:       this.sharedService.getRequest('/sync_order/order_sync_se_to_lw/last'),
            quarantineRetry: this.sharedService.getRequest('/sync_order/order_quarantine_retry/last'),
        })
        .pipe(takeUntil(this.destroy$), finalize(() => { this.loadingOrderStats = false; }))
        .subscribe({
            next: (res: any) => {
                this.orderSyncStat           = this.mapOrderRun(res.orderSync);
                this.orderQuarantineRetryStat = this.mapOrderRun(res.quarantineRetry);
            },
            error: () => { this.toastr.error('Could not load order sync stats.'); },
        });
    }

    private mapOrderRun(raw: any): OrderCronRun | null {
        if (!raw) return null;
        let result = raw.result ?? null;
        if (!result && raw.result_summary) {
            if (typeof raw.result_summary === 'string') {
                try { result = JSON.parse(raw.result_summary); }
                catch { result = null; }
            } else {
                result = raw.result_summary;
            }
        }
        return {
            ...raw,
            finished_at: raw.finished_at ?? raw.completed_at ?? null,
            result,
        };
    }

    // ── Order sync — 7-day aggregates + cursor ───────────────────────────────────────────

    loadOrderAggStats(): void {
        this.loadingOrderAgg = true;
        forkJoin({
            agg:        this.sharedService.getRequest('/sync_order/order/stats?days=7'),
            cursors:    this.sharedService.getRequest('/sync_order/order/cursors'),
            quarantine: this.sharedService.getRequest('/sync_order/order/quarantine_summary'),
        })
        .pipe(takeUntil(this.destroy$), finalize(() => { this.loadingOrderAgg = false; }))
        .subscribe({
            next: (res: any) => {
                const rows: OrderCronAggStats[] = res.agg?.items ?? res.agg ?? [];
                this.orderSyncAgg           = rows.find(r => r.job_name === 'order_sync_se_to_lw') ?? null;
                this.orderQuarantineRetryAgg = rows.find(r => r.job_name === 'order_quarantine_retry') ?? null;

                const cursors: SyncCursor[] = res.cursors?.items ?? res.cursors ?? [];
                this.cursorOrderSync = cursors.find(c => c.name === 'order_sync_se_to_lw') ?? null;

                this.orderQuarantineSummary = res.quarantine ?? null;
            },
            error: () => { /* non-critical */ },
        });
    }

    // ── View helpers for order cron cards ────────────────────────────────────────────────

    getOrderCronCardClass(run: OrderCronRun | null): string {
        if (!run) return 'cron-card-unknown';
        if (run.status === 'running') return 'cron-card-running';
        if (run.status === 'failed')  return 'cron-card-failed';
        if ((run.result?.failed ?? 0) > 0 || (run.result?.quarantined ?? 0) > 0) return 'cron-card-partial';
        if (run.status === 'success') return 'cron-card-success';
        return 'cron-card-unknown';
    }
    getOrderCronIconClass(run: OrderCronRun | null): string {
        if (!run) return 'cron-icon-unknown';
        if (run.status === 'running') return 'cron-icon-running';
        if (run.status === 'failed')  return 'cron-icon-failed';
        if ((run.result?.failed ?? 0) > 0 || (run.result?.quarantined ?? 0) > 0) return 'cron-icon-partial';
        if (run.status === 'success') return 'cron-icon-success';
        return 'cron-icon-unknown';
    }
    getOrderCronStatusClass(run: OrderCronRun | null): string {
        if (!run) return 'cron-badge-unknown';
        if (run.status === 'running') return 'cron-badge-running';
        if (run.status === 'failed')  return 'cron-badge-failed';
        if ((run.result?.failed ?? 0) > 0 || (run.result?.quarantined ?? 0) > 0) return 'cron-badge-partial';
        if (run.status === 'success') return 'cron-badge-success';
        return 'cron-badge-unknown';
    }
    getOrderCronDotClass(run: OrderCronRun | null): string {
        if (!run) return 'dot-unknown';
        if (run.status === 'running') return 'dot-running';
        if (run.status === 'failed')  return 'dot-failed';
        if ((run.result?.failed ?? 0) > 0 || (run.result?.quarantined ?? 0) > 0) return 'dot-partial';
        if (run.status === 'success') return 'dot-success';
        return 'dot-unknown';
    }
    getOrderCronStatusLabel(run: OrderCronRun | null): string {
        if (!run) return 'No data';
        if (run.status === 'running') return 'Running';
        if (run.status === 'failed')  return 'Failed';
        if ((run.result?.failed ?? 0) > 0 || (run.result?.quarantined ?? 0) > 0) return 'Partial';
        if (run.status === 'success') return 'Success';
        return 'Unknown';
    }

    // ── 7-day aggregates + cursors ─────────────────────────────────────────────

    loadAggStats(): void {
        this.loadingAgg = true;
        forkJoin({
            agg:     this.sharedService.getRequest('/sync_inventory/inventory/stats?days=7'),
            cursors: this.sharedService.getRequest('/sync_inventory/inventory/cursors'),
        })
        .pipe(takeUntil(this.destroy$), finalize(() => { this.loadingAgg = false; }))
        .subscribe({
            next: (res: any) => {
                const rows: CronAggStats[] = res.agg?.items ?? res.agg ?? [];
                this.deltaAgg = rows.find(r => r.job_name === 'inventory_delta_sync') ?? null;
                this.reconAgg  = rows.find(r => r.job_name === 'inventory_full_recon')  ?? null;

                const cursors: SyncCursor[] = res.cursors?.items ?? res.cursors ?? [];
                this.cursorDelta = cursors.find(c => c.name === 'lw_stock_delta') ?? null;
                this.cursorRecon  = cursors.find(c => c.name === 'lw_stock_recon')  ?? null;
            },
            error: () => { /* non-critical */ },
        });
    }

    // ── View helpers: integration ──────────────────────────────────────────────

    getStatusLabel(status: ConnectionStatus): string {
        return { connected: 'Connected', disconnected: 'Disconnected', error: 'Error', checking: 'Checking...' }[status];
    }
    getCardClass(status: ConnectionStatus): string {
        return { connected: 'card-connected', disconnected: 'card-disconnected', error: 'card-error', checking: 'card-checking' }[status];
    }
    getLogoClass(status: ConnectionStatus): string {
        return { connected: 'logo-connected', disconnected: 'logo-disconnected', error: 'logo-error', checking: 'logo-checking' }[status];
    }
    getBadgeClass(status: ConnectionStatus): string {
        return { connected: 'badge-connected', disconnected: 'badge-disconnected', error: 'badge-error', checking: 'badge-checking' }[status];
    }
    getDotClass(status: ConnectionStatus): string {
        return { connected: 'dot-connected', disconnected: 'dot-disconnected', error: 'dot-error', checking: 'dot-checking' }[status];
    }

    // ── View helpers: cron cards ───────────────────────────────────────────────

    getCronCardClass(run: CronRun | null): string {
        if (!run) return 'cron-card-unknown';
        if (run.status === 'running') return 'cron-card-running';
        if (run.status === 'failed') return 'cron-card-failed';
        if ((run.result?.failed ?? 0) > 0) return 'cron-card-partial';
        if (run.result?.action_required) return 'cron-card-warn';
        if (run.status === 'success') return 'cron-card-success';
        return 'cron-card-unknown';
    }
    getCronIconClass(run: CronRun | null): string {
        if (!run) return 'cron-icon-unknown';
        if (run.status === 'running') return 'cron-icon-running';
        if (run.status === 'failed') return 'cron-icon-failed';
        if ((run.result?.failed ?? 0) > 0) return 'cron-icon-partial';
        if (run.result?.action_required) return 'cron-icon-warn';
        if (run.status === 'success') return 'cron-icon-success';
        return 'cron-icon-unknown';
    }
    getCronStatusClass(run: CronRun | null): string {
        if (!run) return 'cron-badge-unknown';
        if (run.status === 'running') return 'cron-badge-running';
        if (run.status === 'failed') return 'cron-badge-failed';
        if ((run.result?.failed ?? 0) > 0) return 'cron-badge-partial';
        if (run.result?.action_required) return 'cron-badge-warn';
        if (run.status === 'success') return 'cron-badge-success';
        return 'cron-badge-unknown';
    }
    getCronDotClass(run: CronRun | null): string {
        if (!run) return 'dot-unknown';
        if (run.status === 'running') return 'dot-running';
        if (run.status === 'failed') return 'dot-failed';
        if ((run.result?.failed ?? 0) > 0) return 'dot-partial';
        if (run.result?.action_required) return 'dot-warn';
        if (run.status === 'success') return 'dot-success';
        return 'dot-unknown';
    }
    getCronStatusLabel(run: CronRun | null): string {
        if (!run) return 'No data';
        if (run.status === 'running') return 'Running';
        if (run.status === 'failed') return 'Failed';
        if ((run.result?.failed ?? 0) > 0) return 'Partial failure';
        if (run.result?.action_required) return 'Action required';
        if (run.status === 'success') return 'Success';
        return 'Unknown';
    }

    // ── Formatting helpers ─────────────────────────────────────────────────────

    /**
     * Formats an API datetime string exactly as received — no timezone conversion.
     * The API already returns times in Europe/London (BST/GMT), so we parse the
     * date parts directly from the string to avoid any browser-local reinterpretation.
     * Appends "BST" as the label since the server is always on Europe/London.
     *
     * For the live clock (a real JS Date), falls back to Europe/London formatting.
     */
    formatDateTime(date: Date | string | null | undefined): string {
        if (!date) return '—';

        // If it's a raw API string (no Z, no offset) — parse parts directly
        if (typeof date === 'string' && !date.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(date)) {
            // Expected format: "YYYY-MM-DDTHH:mm:ss" or "YYYY-MM-DD HH:mm:ss"
            const match = date.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);
            if (match) {
                const [, y, mo, d, h, mi, s] = match;
                return `${d}/${mo}/${y}, ${h}:${mi}:${s} BST`;
            }
        }

        // Fallback for real Date objects (e.g. live clock) or strings with offset/Z
        const d = new Date(date as string);
        if (isNaN(d.getTime())) return '—';
        const tzLabel = new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Europe/London',
            timeZoneName: 'short',
        }).formatToParts(d).find(p => p.type === 'timeZoneName')?.value ?? 'GMT';
        return d.toLocaleString('en-GB', {
            timeZone: 'Europe/London',
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false,
        }) + ' ' + tzLabel;
    }

    formatDuration(seconds: number | null | undefined): string {
        if (seconds == null) return '—';
        seconds = Math.round(seconds * 100) / 100;
        if (seconds < 60) return `${seconds}s`;
        const m = Math.floor(seconds / 60);
        const s = Math.round((seconds % 60) * 100) / 100;
        return s > 0 ? `${m}m ${s}s` : `${m}m`;
    }
}